// solver.ts — palette role definitions, loss function, gradient-descent step,
// and seed palettes. Numerical (finite-difference) gradients are cheap enough
// here: ~19 roles × 3 OKLCH params × 2 evaluations per param ≈ 114 loss
// evaluations per step, each one converting a dozen colors. Easily real-time.

import type { OKLCH } from './oklch';
import { oklchToRgb, contrastRatio, gamutExcess } from './oklch';
import type { Vision } from './cvd';
import { visionContrast, simulatedDistance, SEPARATION_MIN } from './cvd';

export const ROLES = [
    'bg',
    'surface',
    'border',
    'text',
    'textMuted',
    'primary',
    'onPrimary',
    'secondary',
    'onSecondary',
    'link',
    'success',
    'onSuccess',
    'warning',
    'onWarning',
    'danger',
    'onDanger',
    'info',
    'onInfo',
    'code',
] as const;
export type Role = (typeof ROLES)[number];

export type Mode = 'light' | 'dark';
export type Palette = Record<Role, OKLCH>;
export type LockMap = Partial<Record<Role, boolean>>;

export interface PaletteSet {
    light: Palette;
    dark: Palette;
    locks: { light: LockMap; dark: LockMap };
}

export interface SolverContext {
    mode: Mode;
    primaryHueAnchor?: number;   // soft-anchor for primary hue when not locked
    secondaryHueAnchor?: number; // soft-anchor for secondary hue when not locked
    /**
     * Colour-vision model the palette must additionally satisfy. Defaults to
     * `normal`. Anything else *adds* a second pass of the contrast and
     * separation criteria, measured on simulated colours — it never replaces
     * the trichromat pass, because the palette still has to work for
     * everybody else.
     */
    vision?: Vision;
}

export interface PairSpec { a: Role; b: Role; min: number; w: number }
interface ChromaSpec { role: Role; min?: number; max: number; w: number }
interface HueSpec { role: Role; target: number; tol: number; w: number }

// Foreground-on-background legibility floors, in WCAG contrast ratios. These
// do not move when a colour-vision model is active — see cvd.ts. What moves
// is the colour each ratio is measured on.
export const CONTRAST_PAIRS: PairSpec[] = [
    { a: 'text', b: 'bg', min: 7.0, w: 3.0 },
    { a: 'textMuted', b: 'bg', min: 4.5, w: 2.0 },
    { a: 'text', b: 'surface', min: 5.5, w: 1.5 },
    { a: 'border', b: 'bg', min: 3.0, w: 2.0 },
    { a: 'primary', b: 'bg', min: 3.0, w: 2.5 },
    { a: 'onPrimary', b: 'primary', min: 4.5, w: 2.5 },
    { a: 'secondary', b: 'bg', min: 3.0, w: 2.0 },
    { a: 'onSecondary', b: 'secondary', min: 4.5, w: 2.0 },
    { a: 'link', b: 'bg', min: 4.5, w: 2.0 },
    { a: 'success', b: 'bg', min: 3.0, w: 1.5 },
    { a: 'onSuccess', b: 'success', min: 4.5, w: 1.5 },
    { a: 'warning', b: 'bg', min: 3.0, w: 1.5 },
    { a: 'onWarning', b: 'warning', min: 4.5, w: 1.5 },
    { a: 'danger', b: 'bg', min: 3.0, w: 1.5 },
    { a: 'onDanger', b: 'danger', min: 4.5, w: 1.5 },
    { a: 'info', b: 'bg', min: 3.0, w: 1.5 },
    { a: 'onInfo', b: 'info', min: 4.5, w: 1.5 },
    { a: 'code', b: 'bg', min: 4.5, w: 1.5 },
    { a: 'code', b: 'surface', min: 3.5, w: 1.0 },
];

// Chroma corridors: above max → garish; below min → washed out.
const CHROMA: ChromaSpec[] = [
    { role: 'bg', max: 0.04, w: 2.0 },
    { role: 'surface', max: 0.04, w: 2.0 },
    { role: 'border', max: 0.06, w: 1.0 },
    { role: 'text', max: 0.04, w: 1.0 },
    { role: 'textMuted', max: 0.05, w: 1.0 },
    { role: 'primary', min: 0.10, max: 0.18, w: 1.5 },
    { role: 'onPrimary', max: 0.08, w: 1.0 },
    { role: 'secondary', min: 0.08, max: 0.16, w: 1.5 },
    { role: 'onSecondary', max: 0.08, w: 1.0 },
    { role: 'link', min: 0.08, max: 0.18, w: 1.0 },
    { role: 'success', min: 0.10, max: 0.20, w: 1.0 },
    { role: 'onSuccess', max: 0.08, w: 1.0 },
    { role: 'warning', min: 0.10, max: 0.20, w: 1.0 },
    { role: 'onWarning', max: 0.08, w: 1.0 },
    { role: 'danger', min: 0.12, max: 0.22, w: 1.0 },
    { role: 'onDanger', max: 0.08, w: 1.0 },
    { role: 'info', min: 0.10, max: 0.20, w: 1.0 },
    { role: 'onInfo', max: 0.08, w: 1.0 },
    { role: 'code', min: 0.08, max: 0.18, w: 1.0 },
];

// Semantic hue families — keep success≈green, warning≈yellow-orange, etc.
const HUES: HueSpec[] = [
    { role: 'success', target: 145, tol: 25, w: 2.0 },
    { role: 'warning', target: 75, tol: 25, w: 2.0 },
    { role: 'danger', target: 25, tol: 25, w: 2.0 },
    { role: 'info', target: 235, tol: 25, w: 2.0 },
];

const SURFACE_BG_RATIO = { min: 1.05, max: 1.6, w: 1.0 };
const SEMANTIC_PAIRS: Role[] = ['success', 'warning', 'danger', 'info'];

// Pairs that carry *different meanings* and so must stay tellable apart, as
// opposed to the CONTRAST pairs above which are foreground-on-background
// legibility. Under a CVD model these are the ones that quietly collapse:
// hue distance says success and danger are 120° apart, and a deuteranope sees
// the same colour twice.
export interface SeparateSpec {
    a: Role;
    b: Role;
    w: number;
    /**
     * Fraction of the vision model's ΔE floor this pair has to clear.
     * Defaults to all of it. Only the brand accents get a lower bar: a preset
     * pins primary and secondary to hues 40° apart as a deliberate design
     * choice, and mistaking one brand accent for the other costs a reader
     * nothing — mistaking success for danger costs them the meaning.
     */
    floor?: number;
}

export const SEPARATE_PAIRS: SeparateSpec[] = (() => {
    const out: SeparateSpec[] = [];
    for (let i = 0; i < SEMANTIC_PAIRS.length; i++) {
        for (let j = i + 1; j < SEMANTIC_PAIRS.length; j++) {
            out.push({ a: SEMANTIC_PAIRS[i], b: SEMANTIC_PAIRS[j], w: 1.5 });
        }
    }
    // A link is identified by its colour against the body text around it.
    out.push({ a: 'link', b: 'text', w: 1.0 });
    out.push({ a: 'primary', b: 'secondary', w: 0.75, floor: 0.75 });
    return out;
})();

/** The ΔE floor `pair` has to clear under `vision`. */
export function separationFloor(pair: SeparateSpec, vision: Vision): number {
    return SEPARATION_MIN[vision] * (pair.floor ?? 1);
}

// Cost of two roles collapsing into each other completely, in the same units
// as a contrast deficit of 1.0. The deficit is normalized by the threshold
// first, so this term is bounded no matter how small the ΔE floor is — a pair
// the user has locked into an unsatisfiable corner leans on the palette
// instead of tearing it apart.
const SEPARATION_COST = 0.6;

function hueDist(a: number, b: number): number {
    let d = ((a - b) % 360 + 360) % 360;
    if (d > 180) d = 360 - d;
    return d;
}

const sq = (x: number) => x * x;
// Soft hinge: zero on the satisfied side, quadratic on the violated side.
const hinge = (deficit: number) => (deficit > 0 ? sq(deficit) : 0);

const NORMAL_ONLY: Vision[] = ['normal'];

// A CVD mode layers on top of the trichromat criteria instead of swapping
// them out, so a palette solved for deuteranopia is still a valid palette for
// everyone. Returns the list of models the loss is evaluated under.
function activeVisions(ctx: SolverContext): Vision[] {
    const v = ctx.vision ?? 'normal';
    return v === 'normal' ? NORMAL_ONLY : ['normal', v];
}

export function computeLoss(p: Palette, ctx: SolverContext): number {
    let loss = 0;
    const visions = activeVisions(ctx);

    for (const vision of visions) {
        for (const c of CONTRAST_PAIRS) {
            const r = visionContrast(p[c.a], p[c.b], vision);
            loss += c.w * hinge(c.min - r);
        }
        for (const s of SEPARATE_PAIRS) {
            const floor = separationFloor(s, vision);
            const d = simulatedDistance(p[s.a], p[s.b], vision);
            if (d < floor) loss += s.w * SEPARATION_COST * sq((floor - d) / floor);
        }
    }

    for (const c of CHROMA) {
        const C = p[c.role].C;
        if (c.min !== undefined && C < c.min) loss += c.w * sq(c.min - C) * 100;
        if (C > c.max) loss += c.w * sq(C - c.max) * 100;
    }

    for (const h of HUES) {
        const d = hueDist(p[h.role].h, h.target);
        if (d > h.tol) {
            const over = (d - h.tol) / 180;
            loss += h.w * sq(over) * 10;
        }
    }

    {
        const r = contrastRatio(oklchToRgb(p.surface), oklchToRgb(p.bg));
        if (r < SURFACE_BG_RATIO.min) loss += SURFACE_BG_RATIO.w * sq(SURFACE_BG_RATIO.min - r) * 20;
        if (r > SURFACE_BG_RATIO.max) loss += SURFACE_BG_RATIO.w * sq(r - SURFACE_BG_RATIO.max);
    }

    // Distinct semantic hues: penalize close pairs.
    for (let i = 0; i < SEMANTIC_PAIRS.length; i++) {
        for (let j = i + 1; j < SEMANTIC_PAIRS.length; j++) {
            const d = hueDist(p[SEMANTIC_PAIRS[i]].h, p[SEMANTIC_PAIRS[j]].h);
            if (d < 50) loss += sq((50 - d) / 50) * 3;
        }
    }

    if (ctx.primaryHueAnchor !== undefined) {
        const dp = hueDist(p.primary.h, ctx.primaryHueAnchor);
        loss += sq(dp / 180) * 4;
        const dl = hueDist(p.link.h, ctx.primaryHueAnchor);
        loss += sq(dl / 180) * 2;
        // onPrimary should sit in same hue family as primary.
        const dop = hueDist(p.onPrimary.h, p.primary.h);
        loss += sq(dop / 180) * 1;
    }

    if (ctx.secondaryHueAnchor !== undefined) {
        const ds = hueDist(p.secondary.h, ctx.secondaryHueAnchor);
        loss += sq(ds / 180) * 4;
        const dos = hueDist(p.onSecondary.h, p.secondary.h);
        loss += sq(dos / 180) * 1;
    }

    // on-* semantic foregrounds inherit the parent role's hue so a tinted
    // text on a tinted bg keeps a visually-coherent hue family.
    const ON_PAIRS: Array<[Role, Role]> = [
        ['onSuccess', 'success'],
        ['onWarning', 'warning'],
        ['onDanger', 'danger'],
        ['onInfo', 'info'],
    ];
    for (const [on, parent] of ON_PAIRS) {
        const d = hueDist(p[on].h, p[parent].h);
        loss += sq(d / 180) * 1;
    }

    for (const r of ROLES) loss += 8.0 * gamutExcess(p[r]);

    return loss;
}

// --- gradient descent ---------------------------------------------------

const STEP_EPS = 1e-3;

export function packPalette(p: Palette): number[] {
    const out: number[] = [];
    for (const r of ROLES) out.push(p[r].L, p[r].C, p[r].h);
    return out;
}

export function unpackPalette(arr: number[]): Palette {
    const out = {} as Palette;
    ROLES.forEach((r, i) => {
        out[r] = { L: arr[i * 3], C: arr[i * 3 + 1], h: arr[i * 3 + 2] };
    });
    return out;
}

function clampParam(i: number, v: number): number {
    const kind = i % 3;
    if (kind === 0) return Math.max(0, Math.min(1, v));
    if (kind === 1) return Math.max(0, Math.min(0.4, v));
    let h = v % 360;
    if (h < 0) h += 360;
    return h;
}

export interface StepResult {
    palette: Palette;
    loss: number;
    gradNorm: number;
    /**
     * True when no step size along the gradient improved the loss, so the
     * palette was returned unchanged. Callers should stop iterating: either
     * we're at an optimum, or we're against a constraint that can't be
     * satisfied and this is the best the palette gets.
     */
    stalled: boolean;
}

// How many times the step is halved before giving up on a descent direction.
// 10 halvings spans three orders of magnitude, which is enough to get under
// any hinge whose slope has blown up.
const BACKTRACK_STEPS = 10;

export function gradientStep(
    palette: Palette,
    locks: LockMap,
    ctx: SolverContext,
    lr = 0.04,
): StepResult {
    const x = packPalette(palette);
    const grad = new Array<number>(x.length).fill(0);

    for (let i = 0; i < x.length; i++) {
        const role = ROLES[Math.floor(i / 3)];
        if (locks[role]) continue;
        const orig = x[i];
        // Larger step for hue (degrees) to keep finite-difference numerical
        // gradient in a comparable scale to L (0..1) and C (0..0.4).
        const eps = i % 3 === 2 ? 0.5 : STEP_EPS;
        x[i] = orig + eps;
        const lp = computeLoss(unpackPalette(x), ctx);
        x[i] = orig - eps;
        const lm = computeLoss(unpackPalette(x), ctx);
        x[i] = orig;
        grad[i] = (lp - lm) / (2 * eps);
    }

    let gNorm = 0;
    for (let i = 0; i < x.length; i++) {
        if (locks[ROLES[Math.floor(i / 3)]]) continue;
        gNorm += grad[i] * grad[i];
    }

    // Per-channel learning-rate scaling so hue (large absolute scale) and
    // L/C (0..1 scale) move at compatible visual rates.
    const stepped = (mult: number): Palette => {
        const y = x.slice();
        for (let i = 0; i < y.length; i++) {
            if (locks[ROLES[Math.floor(i / 3)]]) continue;
            const scale = (i % 3 === 2 ? lr * 2000 : lr * 0.5) * mult;
            y[i] = clampParam(i, y[i] - scale * grad[i]);
        }
        return unpackPalette(y);
    };

    // Backtracking line search. A hinge's slope blows up as one of its colors
    // approaches black — the contrast ratio has a pole there — so a deeply
    // violated constraint produces a gradient large enough that the raw step
    // slams roles into the L/C clamps, whereupon the descent bang-bangs
    // between them and never settles. Halving until the loss actually drops
    // makes every step monotone, which is what turns a violation the palette
    // can't fully satisfy into a best effort instead of a wrecked palette.
    const baseLoss = computeLoss(palette, ctx);
    let mult = 1;
    for (let t = 0; t < BACKTRACK_STEPS; t++) {
        const candidate = stepped(mult);
        const loss = computeLoss(candidate, ctx);
        if (loss < baseLoss) {
            return { palette: candidate, loss, gradNorm: Math.sqrt(gNorm), stalled: false };
        }
        mult *= 0.5;
    }
    return { palette, loss: baseLoss, gradNorm: Math.sqrt(gNorm), stalled: true };
}

export interface SolveOptions {
    /** Stop once `gradNorm` falls below this threshold. */
    tolerance?: number;
    /** Hard cap on iterations — solver bails out even if not converged. */
    maxSteps?: number;
    /** Learning rate forwarded to `gradientStep`. */
    lr?: number;
}

// Run gradient descent until the gradient norm drops under `tolerance` or
// `maxSteps` is hit. Used to bake a contrast-optimized default palette at
// app boot so users see a solved theme without ever opening ThemePage.
export function solveToConvergence(
    palette: Palette,
    locks: LockMap,
    ctx: SolverContext,
    opts: SolveOptions = {},
): Palette {
    const tolerance = opts.tolerance ?? 1e-3;
    const maxSteps = opts.maxSteps ?? 800;
    const lr = opts.lr ?? 0.04;
    let p = palette;
    for (let i = 0; i < maxSteps; i++) {
        const r = gradientStep(p, locks, ctx, lr);
        p = r.palette;
        if (r.stalled || r.gradNorm < tolerance) break;
    }
    return p;
}

// --- seed palettes ------------------------------------------------------

const seed = (L: number, C: number, h: number): OKLCH => ({ L, C, h });

// Dark mode: semantic colors live in the L=0.65–0.78 band so dark text
// (L≈0.10) reads cleanly on top. Secondary is brand-colored — same dark-mode
// L band — and onSecondary mirrors the dark-text pattern.
export const SEED_DARK_SEPIA: Palette = {
    bg: seed(0.16, 0.012, 80),
    surface: seed(0.22, 0.012, 80),
    border: seed(0.32, 0.020, 80),
    text: seed(0.95, 0.010, 80),
    textMuted: seed(0.65, 0.010, 80),
    primary: seed(0.72, 0.140, 220),
    onPrimary: seed(0.10, 0.020, 220),
    secondary: seed(0.72, 0.120, 260),
    onSecondary: seed(0.10, 0.020, 260),
    link: seed(0.72, 0.120, 240),
    success: seed(0.72, 0.150, 145),
    onSuccess: seed(0.10, 0.020, 145),
    warning: seed(0.78, 0.150, 75),
    onWarning: seed(0.10, 0.020, 75),
    danger: seed(0.65, 0.180, 25),
    onDanger: seed(0.10, 0.020, 25),
    info: seed(0.72, 0.130, 235),
    onInfo: seed(0.10, 0.020, 235),
    code: seed(0.74, 0.140, 333),
};

// Light mode: semantic colors sit at L=0.50–0.62 and on-* colors default
// to near-white (L=0.98). Warning is the awkward case — yellow at L=0.62
// has too little contrast against white text — but the solver pulls the
// on-* L down via the contrast hinge as needed.
export const SEED_LIGHT_SEPIA: Palette = {
    bg: seed(0.97, 0.012, 80),
    surface: seed(0.99, 0.005, 80),
    border: seed(0.78, 0.020, 80),
    text: seed(0.18, 0.012, 80),
    textMuted: seed(0.45, 0.010, 80),
    primary: seed(0.52, 0.150, 220),
    onPrimary: seed(0.98, 0.005, 220),
    secondary: seed(0.52, 0.130, 260),
    onSecondary: seed(0.98, 0.005, 260),
    link: seed(0.45, 0.150, 240),
    success: seed(0.50, 0.150, 145),
    onSuccess: seed(0.98, 0.005, 145),
    warning: seed(0.62, 0.150, 75),
    onWarning: seed(0.15, 0.020, 75),
    danger: seed(0.50, 0.180, 25),
    onDanger: seed(0.98, 0.005, 25),
    info: seed(0.50, 0.140, 235),
    onInfo: seed(0.98, 0.005, 235),
    code: seed(0.45, 0.150, 333),
};

export interface Preset {
    id: string;
    name: string;
    description: string;
    primaryHue: number;
    /**
     * Hue anchor for the secondary brand accent. Defaults to
     * `primaryHue + 40` (mod 360) — far enough to read as a distinct
     * accent without colliding with the primary hue family.
     */
    secondaryHue?: number;
    bgLight: OKLCH;
    bgDark: OKLCH;
}

export const PRESETS: Preset[] = [
    {
        id: 'sepia',
        name: 'Sepia',
        description: 'Warm cream paper / charcoal ink. Dark mode keeps a faint amber undertone.',
        primaryHue: 220,
        bgLight: seed(0.97, 0.012, 80),
        bgDark: seed(0.16, 0.012, 80),
    },
    {
        id: 'slate',
        name: 'Slate',
        description: 'Cool neutral greys. Modern, low-temperature.',
        primaryHue: 250,
        bgLight: seed(0.98, 0.005, 250),
        bgDark: seed(0.18, 0.012, 250),
    },
    {
        id: 'forest',
        name: 'Forest',
        description: 'Mossy paper / deep pine. Earthy and saturated.',
        primaryHue: 175,
        bgLight: seed(0.96, 0.020, 110),
        bgDark: seed(0.15, 0.018, 150),
    },
    {
        id: 'rose',
        name: 'Rose',
        description: 'Faintly pink paper / aubergine ink.',
        primaryHue: 340,
        bgLight: seed(0.97, 0.018, 15),
        bgDark: seed(0.17, 0.020, 350),
    },
    // Solarized — Ethan Schoonover. Light bg #fdf6e3 (warm cream), dark bg
    // #002b36 (deep teal). Primary anchored to Solarized blue (#268bd2).
    {
        id: 'solarized',
        name: 'Solarized',
        description: 'Ethan Schoonover’s Solarized — cream / deep teal with a Solarized-blue accent.',
        primaryHue: 245,
        bgLight: seed(0.974, 0.026, 90),
        bgDark: seed(0.267, 0.049, 220),
    },
    // Tango — GNOME Tango palette. Cool aluminium light / charcoal dark with
    // the Tango sky-blue (#3465a4) as the primary accent.
    {
        id: 'tango',
        name: 'Tango',
        description: 'GNOME Tango palette — cool aluminium / charcoal with Tango sky-blue accent.',
        primaryHue: 256,
        bgLight: seed(0.949, 0.003, 106),
        bgDark: seed(0.320, 0.009, 220),
    },
];

export function makeInitialSet(preset: Preset): PaletteSet {
    const secondaryHue = preset.secondaryHue ?? (preset.primaryHue + 40) % 360;
    const tinted = (base: Palette, bg: OKLCH, mode: Mode): Palette => {
        const p: Palette = { ...base };
        p.bg = bg;
        // Shift bg-derived neutrals toward bg's hue and slightly tint them.
        const hueLerp = (h: number, t: number) => {
            const dh = ((bg.h - h + 540) % 360) - 180;
            return ((h + dh * t) + 360) % 360;
        };
        for (const r of ['surface', 'border', 'text', 'textMuted'] as Role[]) {
            p[r] = { ...p[r], h: hueLerp(p[r].h, 1.0) };
        }
        p.surface = mode === 'dark'
            ? { ...p.surface, L: Math.min(1, bg.L + 0.06) }
            : { ...p.surface, L: Math.min(1, bg.L + 0.02) };
        p.primary = { ...p.primary, h: preset.primaryHue };
        p.onPrimary = { ...p.onPrimary, h: preset.primaryHue };
        p.secondary = { ...p.secondary, h: secondaryHue };
        p.onSecondary = { ...p.onSecondary, h: secondaryHue };
        p.link = { ...p.link, h: (preset.primaryHue + 20) % 360 };
        return p;
    };
    return {
        light: tinted(SEED_LIGHT_SEPIA, preset.bgLight, 'light'),
        dark: tinted(SEED_DARK_SEPIA, preset.bgDark, 'dark'),
        locks: { light: { bg: true }, dark: { bg: true } },
    };
}

export const ROLE_LABELS: Record<Role, string> = {
    bg: 'Page Background',
    surface: 'Card Surface',
    border: 'Border / Outline',
    text: 'Body Text',
    textMuted: 'Muted Text',
    primary: 'Primary / Brand',
    onPrimary: 'On Primary',
    secondary: 'Secondary',
    onSecondary: 'On Secondary',
    link: 'Link',
    success: 'Success',
    onSuccess: 'On Success',
    warning: 'Warning',
    onWarning: 'On Warning',
    danger: 'Danger',
    onDanger: 'On Danger',
    info: 'Info',
    onInfo: 'On Info',
    code: 'Code / Monospace',
};
